import React, { useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Circle, CircleMarker, Polygon, Polyline, Tooltip, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import { useLanguage } from '../../contexts/LanguageContext';
import { sectorsFor, mapViewFor, type Coord } from '../../data/communications';
import { useCity } from '../../contexts/CityContext';
import { householdsNear } from '../../utils/households';
import type { AudiencePlace } from '../../types/composer';
const addressIcon = L.divIcon({
  className: '',
  html: `<div style="width:16px;height:16px;border-radius:9999px;background:#4F46E5;border:3px solid white;box-shadow:0 0 0 2px rgba(79,70,229,0.35);"></div>`,
  iconSize: [16, 16],
  iconAnchor: [8, 8]
});

/** Captures map clicks while the user traces a custom polygon. */
function DrawCapture({
  active,
  onAddPoint
}: {
  active: boolean;
  onAddPoint: (c: Coord) => void;
}) {
  useMapEvents({
    click(e) {
      if (active) onAddPoint([e.latlng.lat, e.latlng.lng]);
    }
  });
  return null;
}

/** Pans to the most recently added address or radius. */
function FocusOn({
  coord
}: {
  coord: Coord | null;
}) {
  const map = useMap();
  useEffect(() => {
    if (coord) map.panTo(coord);
  }, [coord, map]);
  return null;
}
interface AudienceMapProps {
  places: AudiencePlace[];
  onToggleSector: (sectorId: string) => void;
  drawing: boolean;
  draftPoints: Coord[];
  onAddDraftPoint: (c: Coord) => void;
}

/** Every place of the audience drawn together, so the union reads at a glance. */
export function AudienceMap({
  places,
  onToggleSector,
  drawing,
  draftPoints,
  onAddDraftPoint
}: AudienceMapProps) {
  const {
    t,
    lang
  } = useLanguage();
  const {
    city
  } = useCity();
  const view = mapViewFor(city);
  const selectedSectors = places.filter((p): p is Extract<AudiencePlace, {
    kind: 'sector';
  }> => p.kind === 'sector').map(p => p.sectorId);
  const lastPoint = useMemo(() => {
    const last = [...places].reverse().find(p => p.kind === 'address' || p.kind === 'radius');
    return last && (last.kind === 'address' || last.kind === 'radius') ? last.coord : null;
  }, [places]);
  return <div className="h-72 rounded-xl overflow-hidden border border-slate-200 relative" data-id="element-3799" data-mp="3n0t9va40uvzr">
      <div className="absolute top-2 left-1/2 -translate-x-1/2 z-[400] pointer-events-none" data-id="element-3800" data-mp="3r36c3ojk8brs">
        <span className={`bg-white/90 backdrop-blur-sm px-4 py-1.5 rounded-full shadow-sm border text-xs font-medium ${drawing ? 'border-indigo-200 text-[#4F46E5]' : 'border-slate-200 text-slate-500'}`} data-id="element-3801" data-mp="2ucxf4ay0je5m">
          {drawing ? t('Cliquez sur la carte pour tracer la zone', 'Click the map to trace the area') : t('Cliquez un secteur pour l’ajouter ou le retirer', 'Click a district to add or remove it')}
        </span>
      </div>

      <MapContainer key={city} center={view.center} zoom={view.zoom} scrollWheelZoom={false} style={{
      height: '100%',
      width: '100%'
    }} data-id="element-3802" data-mp="282xcd589o8bc">
        <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" data-id="element-3803" data-mp="1z2sepmsvkoad" />
        <FocusOn coord={lastPoint} data-id="element-3804" data-mp="1p5nuhsilb2w7" />

        {sectorsFor(city).map(s => {
        const selected = selectedSectors.includes(s.id);
        return <Polygon key={s.id} positions={s.polygon} eventHandlers={{
          click: () => {
            if (!drawing) onToggleSector(s.id);
          }
        }} pathOptions={{
          color: selected ? s.color : '#94A3B8',
          weight: selected ? 2 : 1,
          fillColor: selected ? s.color : '#CBD5E1',
          fillOpacity: selected ? 0.22 : 0.04,
          dashArray: selected ? undefined : '4 4',
          className: drawing ? '' : 'cursor-pointer'
        }} data-id="element-3805" data-mp="1kcb4pf26cq1v">
              <Tooltip direction="center" permanent={selected} data-id="element-3806" data-mp="2rrznq3cjqihs">
                {s.label[lang]}
              </Tooltip>
            </Polygon>;
      })}

        {places.map(place => {
        if (place.kind === 'address') {
          return <Marker key={place.id} position={place.coord} icon={addressIcon} data-id="element-3807" data-mp="2ipqj1u3yljcb">
                <Tooltip direction="top" offset={[0, -8]} opacity={1} data-id="element-3808" data-mp="2mtofty5tai27">
                  <span className="font-semibold" data-id="element-3809" data-mp="28tndee9wf2km">{place.label}</span>
                </Tooltip>
              </Marker>;
        }
        if (place.kind === 'radius') {
          const households = householdsNear(place.coord, Number(place.radius));
          return <React.Fragment key={place.id} data-id="element-3810" data-mp="c0llu9xfpnbj">
                <Circle center={place.coord} radius={Number(place.radius)} pathOptions={{
              color: '#4F46E5',
              fillColor: '#4F46E5',
              fillOpacity: 0.1
            }} data-id="element-3811" data-mp="bziszwgjxasz" />
                <Marker position={place.coord} icon={addressIcon} data-id="element-3812" data-mp="1rhowg18yqa5i" />
                {households.map(h => <CircleMarker key={h.id} center={h.coord} radius={3.5} pathOptions={{
              color: '#ffffff',
              weight: 1.5,
              fillColor: '#4F46E5',
              fillOpacity: 1
            }} data-id="element-3813" data-mp="26aip6od8wms4">
                    <Tooltip direction="top" offset={[0, -6]} opacity={1} data-id="element-3814" data-mp="2p71qwslpz8en">
                      <span className="font-semibold" data-id="element-3815" data-mp="2k9icnsp0uvmp">{h.address}</span>
                    </Tooltip>
                  </CircleMarker>)}
              </React.Fragment>;
        }
        if (place.kind === 'area') {
          return <Polygon key={place.id} positions={place.points} pathOptions={{
            color: '#4F46E5',
            fillColor: '#4F46E5',
            fillOpacity: 0.15
          }} data-id="element-3816" data-mp="1fil9bbcm9s6j" />;
        }
        return null;
      })}

        {/* Area being traced */}
        {draftPoints.length >= 3 && <Polygon positions={draftPoints} pathOptions={{
        color: '#4F46E5',
        fillColor: '#4F46E5',
        fillOpacity: 0.1,
        dashArray: '4 4'
      }} data-id="element-3817" data-mp="1tje29rn9sf7j" />}
        {draftPoints.length === 2 && <Polyline positions={draftPoints} pathOptions={{
        color: '#4F46E5'
      }} data-id="element-3818" data-mp="22hhq4ulq9o2i" />}
        {draftPoints.map((p, i) => <Circle key={i} center={p} radius={12} pathOptions={{
        color: '#4F46E5',
        fillColor: '#fff',
        fillOpacity: 1
      }} data-id="element-3819" data-mp="27c3r03u6ny8c" data-mpi={i} />)}
        <DrawCapture active={drawing} onAddPoint={onAddDraftPoint} data-id="element-3820" data-mp="2h5ud522u7wkv" />
      </MapContainer>
    </div>;
}